import React, { useState } from 'react';
import { useEditor, EditorContent, Mark, mergeAttributes, Extension, Node, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import * as FiIcons from 'react-icons/fi';
import { renderToStaticMarkup } from 'react-dom/server';
import StarterKit from '@tiptap/starter-kit';
import pb from '../lib/pocketbase';
import { compressImage } from '../utils/imageCompression';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import './TiptapEditor.css';

// Import dynamique de polices Google Fonts
const fontImport = document.createElement('link');
fontImport.rel = 'stylesheet';
fontImport.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@400..700&family=Dancing+Script:wght@400..700&family=Pacifico&family=Space+Grotesk:wght@300..700&family=Cinzel:wght@400..900&family=Lora:ital,wght@0,400..700;1,400..700&family=Abril+Fatface&family=Lobster&family=Shadows+Into+Light&family=Bebas+Neue&family=Amatic+SC:wght@400;700&family=Comfortaa:wght@300..700&family=Playwrite+FR+Trad:wght@100..400&family=Sour+Gummy:ital,wght@0,100..900;1,100..900&display=swap';
document.head.appendChild(fontImport);

/** Extension FontSize */
const FontSize = Extension.create({
    name: 'fontSize',
    addOptions() { return { types: ['textStyle'] } },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                fontSize: {
                    default: null,
                    parseHTML: element => element.style.fontSize?.replace(/['"]+/g, ''),
                    renderHTML: attributes => {
                        if (!attributes.fontSize) return {}
                        return { style: `font-size: ${attributes.fontSize}` }
                    },
                },
            },
        }]
    },
    addCommands() {
        return {
            setFontSize: fontSize => ({ chain }) => chain().setMark('textStyle', { fontSize }).run(),
            unsetFontSize: () => ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
        }
    },
})

/** Extension FontFamily */
const FontFamily = Extension.create({
    name: 'fontFamily',
    addOptions() { return { types: ['textStyle'] } },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                fontFamily: {
                    default: null,
                    parseHTML: element => element.style.fontFamily?.replace(/['"]+/g, ''),
                    renderHTML: attributes => {
                        if (!attributes.fontFamily) return {}
                        return { style: `font-family: ${attributes.fontFamily}` }
                    },
                },
            },
        }]
    },
    addCommands() {
        return {
            setFontFamily: fontFamily => ({ chain }) => chain().setMark('textStyle', { fontFamily }).run(),
            unsetFontFamily: () => ({ chain }) => chain().setMark('textStyle', { fontFamily: null }).removeEmptyTextStyle().run(),
        }
    },
})


/** Extension LineHeight */

/** Extension LineHeight */
const LineHeight = Extension.create({
    name: 'lineHeight',
    addOptions() { return { types: ['paragraph', 'heading', 'listItem', 'bulletInline', 'orderedInline'], defaultLineHeight: 'normal' } },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                lineHeight: {
                    default: this.options.defaultLineHeight,
                    parseHTML: element => element.style.lineHeight || this.options.defaultLineHeight,
                    renderHTML: attributes => {
                        if (attributes.lineHeight === 'normal') return {}
                        return { style: `line-height: ${attributes.lineHeight}; margin-bottom: ${attributes.lineHeight === '1' ? '0' : ''};` }
                    },
                },
            },
        }]
    },
    addCommands() {
        return {
            setLineHeight: lineHeight => ({ commands }) => {
                return this.options.types.every(type => commands.updateAttributes(type, { lineHeight }))
            },
            unsetLineHeight: () => ({ commands }) => {
                return this.options.types.every(type => commands.updateAttributes(type, { lineHeight: 'normal' }))
            },
        }
    },
})

/** Extension TextAlignInline */
const TextAlignInline = Mark.create({
    name: 'textAlignInline',
    inclusive: true,
    addAttributes() {
        return {
            align: {
                default: 'left',
                parseHTML: element => element.style.textAlign || 'left',
                renderHTML: attributes => {
                    if (attributes.align === 'left') return {}
                    return { style: `display: block; text-align: ${attributes.align}; width: 100%; min-height: 1.2em;` }
                },
            },
        }
    },
    parseHTML() { return [{ tag: 'span', getAttrs: element => element.style.textAlign ? { align: element.style.textAlign } : false }] },
    renderHTML({ HTMLAttributes }) { return ['span', mergeAttributes(HTMLAttributes), 0] },
    addCommands() {
        return {
            setTextAlignInline: align => ({ chain, editor }) => {
                if (align === 'left') return chain().unsetMark(this.name).run()
                if (editor.state.selection.empty) {
                    return chain().setMark(this.name, { align }).insertContent('\u200B').run()
                }
                return chain().setMark(this.name, { align }).run()
            },
        }
    },
})

/** 
 * Extension BulletInline (Puces granulaires)
 */
const BulletInline = Mark.create({
    name: 'bulletInline',
    inclusive: true,
    addAttributes() {
        return {
            active: {
                default: true,
                renderHTML: () => ({ class: 'bullet-inline-item', style: 'display: block; list-style-type: disc; list-style-position: inside; margin-left: 1rem; min-height: 1.2em;' })
            }
        }
    },
    parseHTML() { return [{ tag: 'span', getAttrs: node => node.classList.contains('bullet-inline-item') }] },
    renderHTML({ HTMLAttributes }) { return ['span', mergeAttributes(HTMLAttributes), 0] },
    addCommands() {
        return {
            toggleBulletInline: () => ({ chain, editor }) => {
                if (editor.isActive(this.name)) return chain().unsetMark(this.name).run()
                if (editor.state.selection.empty) {
                    return chain().setMark(this.name).insertContent('\u200B').run()
                }
                return chain().setMark(this.name).run()
            },
        }
    },
})

/** 
 * Extension OrderedInline (Numéros granulaires)
 */
const OrderedInline = Mark.create({
    name: 'orderedInline',
    inclusive: true,
    addAttributes() {
        return {
            active: {
                default: true,
                renderHTML: () => ({ class: 'ordered-inline-item', style: 'display: block; list-style-position: inside; margin-left: 1rem; min-height: 1.2em;' })
            }
        }
    },
    parseHTML() { return [{ tag: 'span', getAttrs: node => node.classList.contains('ordered-inline-item') }] },
    renderHTML({ HTMLAttributes }) { return ['span', mergeAttributes(HTMLAttributes), 0] },
    addCommands() {
        return {
            toggleOrderedInline: () => ({ chain, editor }) => {
                if (editor.isActive(this.name)) return chain().unsetMark(this.name).run()
                if (editor.state.selection.empty) {
                    return chain().setMark(this.name).insertContent('\u200B').run()
                }
                return chain().setMark(this.name).run()
            },
        }
    },
})

/** Mark Accent */
const Accent = Mark.create({
    name: 'accent',
    inclusive: true,
    exitable: true,
    parseHTML() { return [{ tag: 'span', getAttrs: node => node.classList.contains('accent') && null }, { class: 'accent' }] },
    renderHTML({ HTMLAttributes }) { return ['span', mergeAttributes(HTMLAttributes, { class: 'accent' }), 0] },
});

/** CUSTOM RESIZABLE ICON COMPONENT */
const IconNodeView = (props) => {
    const { node, updateAttributes, selected } = props;
    const IconComponent = FiIcons[node.attrs.name] || FiIcons.FiStar;

    const onMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startSize = parseInt(node.attrs.size) || 24;

        const onMouseMove = (moveEvent) => {
            const diff = moveEvent.clientX - startX;
            const newSize = Math.max(12, Math.min(200, startSize + diff));
            updateAttributes({ size: `${newSize}px` });
        };

        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    return (
        <NodeViewWrapper
            as="span"
            className={`pure-icon-container ${selected ? 'is-selected' : ''}`}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: node.attrs.size,
                color: node.attrs.color,
                verticalAlign: 'middle',
                margin: '0 0.1em',
                position: 'relative',
                lineHeight: 0,
                padding: '0',
                background: 'none',
                border: 'none',
                borderRadius: '0',
                outline: 'none'
            }}
        >
            <div data-drag-handle style={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}>
                <IconComponent style={{ pointerEvents: 'none' }} />
            </div>
            {selected && (
                <div
                    style={{
                        position: 'absolute',
                        right: '-4px',
                        bottom: '-4px',
                        width: '7px',
                        height: '7px',
                        background: 'var(--primary, #0ea5e9)',
                        borderRadius: '0',
                        cursor: 'nwse-resize',
                        zIndex: 10,
                        border: '1px solid white'
                    }}
                    onMouseDown={onMouseDown}
                />
            )}
        </NodeViewWrapper>
    );
};

/** EXTENSION ICON */
const IconNode = Node.create({
    name: 'tiptapIcon',
    group: 'inline',
    inline: true,
    atom: true,
    draggable: true,
    addAttributes() {
        return {
            name: { default: 'FiStar' },
            size: { default: '24px' },
            color: { default: 'currentColor' }
        }
    },
    parseHTML() {
        return [{
            tag: 'span[data-tiptap-icon]',
            getAttrs: el => ({
                name: el.getAttribute('data-tiptap-icon'),
                size: el.style.fontSize,
                color: el.style.color
            })
        }]
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-tiptap-icon': HTMLAttributes.name,
                class: 'tiptap-icon',
                style: `display: inline-flex; vertical-align: middle; font-size: ${HTMLAttributes.size}; color: ${HTMLAttributes.color}; line-height: 1;`
            })
        ];
    },
    addNodeView() {
        return ReactNodeViewRenderer(IconNodeView)
    }
});



/** CUSTOM RESIZABLE IMAGE COMPONENT */
const ImageNodeView = (props) => {
    const { node, updateAttributes, selected } = props;
    const [isResizing, setIsResizing] = useState(false);

    const onMouseDown = (e) => {
        e.preventDefault();
        setIsResizing(true);
        const startX = e.clientX;
        const startWidth = parseInt(node.attrs.width) || 300;

        const onMouseMove = (moveEvent) => {
            const diff = moveEvent.clientX - startX;
            const newWidth = Math.max(100, Math.min(1200, startWidth + diff));
            updateAttributes({ width: `${newWidth}px` });
        };

        const onMouseUp = () => {
            setIsResizing(false);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    const wrapperStyle = node.attrs.align === 'center'
        ? { display: 'block', margin: '1.5rem auto', textAlign: 'center', clear: 'both' }
        : { display: 'inline-block', float: node.attrs.align, margin: node.attrs.align === 'left' ? '0 1.5rem 1rem 0' : '0 0 1.25rem 1.5rem', verticalAlign: 'top' };

    return (
        <NodeViewWrapper
            className={`resizable-image-container align-${node.attrs.align || 'center'} ${selected ? 'is-selected' : ''}`}
            style={wrapperStyle}
        >
            <div className="image-wrapper" data-drag-handle style={{ position: 'relative', display: 'inline-block', width: node.attrs.width || '100%', maxWidth: '100%' }}>
                <img src={node.attrs.src} alt="" draggable="false" style={{ width: '100%', display: 'block', borderRadius: '8px' }} />
                {node.attrs.href && (
                    <div style={{ position: 'absolute', top: '5px', left: '5px', background: 'var(--primary, #0ea5e9)', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>LIEN</div>
                )}
                {selected && (
                    <>
                        <div className="resize-handle" onMouseDown={onMouseDown} />
                    </>
                )}
            </div>
        </NodeViewWrapper>
    );
};

/** EXTENSION RESIZABLE IMAGE */
const ResizableImage = Image.extend({
    draggable: true,
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: '300px',
                renderHTML: attributes => ({
                    style: `width: ${attributes.width};`,
                }),
            },
            align: {
                default: 'center',
                renderHTML: attributes => {
                    if (attributes.align === 'left') return { style: 'float: left; margin: 0 1.5rem 1rem 0; clear: none;' }
                    if (attributes.align === 'right') return { style: 'float: right; margin: 0 0 1rem 1.5rem; clear: none;' }
                    return { style: 'display: block; margin: 1.5rem auto; text-align: center; clear: both;' }
                }
            },
            href: {
                default: null,
                renderHTML: attributes => {
                    if (!attributes.href) return {}
                    return { 'data-href': attributes.href }
                }
            }
        }
    },
    renderHTML({ HTMLAttributes }) {
        const { href, ...rest } = HTMLAttributes;
        if (href) {
            return ['a', { href, target: '_blank', rel: 'noopener noreferrer' }, ['img', mergeAttributes(this.options.HTMLAttributes, rest)]]
        }
        return ['img', mergeAttributes(this.options.HTMLAttributes, rest)]
    },
    addNodeView() {
        return ReactNodeViewRenderer(ImageNodeView)
    }
});

const TiptapEditor = ({ content, onChange, placeholder = 'Commencez à écrire...' }) => {
    const [modal, setModal] = useState({ open: false, type: 'link', value: '', title: '' });
    const [uploading, setUploading] = useState(false);
    const [lastSelection, setLastSelection] = useState(null);
    const [, setUpdate] = useState(0);

    const openLinkModal = () => {
        let previousUrl = '';
        if (editor.isActive('image')) {
            previousUrl = editor.getAttributes('image').href || '';
        } else {
            previousUrl = editor.getAttributes('link').href || '';
        }
        setModal({ open: true, type: 'link', value: previousUrl, title: 'Insérer un lien' });
    };

    const saveModalContent = () => {
        if (editor.isActive('image')) {
            editor.chain().focus().updateAttributes('image', { href: modal.value || null }).run();
        } else {
            if (modal.value) {
                editor.chain().focus().extendMarkRange('link').setLink({ href: modal.value }).run();
            } else {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
            }
        }
        setModal({ ...modal, open: false });
    };

    const addImage = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;

            setUploading(true);
            try {
                // Compression
                const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.8 });
                const fd = new FormData();
                fd.append('file', compressed);

                // Upload to PocketBase 'media' collection
                const res = await pb.collection('media').create(fd);
                const url = pb.files.getUrl(res, res.file);

                // Insert into Tiptap
                editor.chain().focus().setImage({ src: url }).run();
            } catch (err) {
                console.error("Upload failed", err);
                alert("Erreur lors de l'upload de l'image.");
            } finally {
                setUploading(false);
            }
        };
        input.click();
    };


    const popularIcons = [
        'FiStar', 'FiHeart', 'FiCheck', 'FiInfo', 'FiAlertTriangle', 'FiBell', 'FiSettings', 'FiUser', 'FiMail', 'FiPhone',
        'FiMapPin', 'FiCalendar', 'FiClock', 'FiSearch', 'FiDownload', 'FiShare2', 'FiExternalLink', 'FiArrowRight',
        'FiChevronRight', 'FiActivity', 'FiAward', 'FiBriefcase', 'FiCoffee', 'FiCpu', 'FiDatabase', 'FiFeather',
        'FiFlag', 'FiGift', 'FiGlobe', 'FiHome', 'FiLayers', 'FiLinkedin', 'FiTwitter', 'FiGithub', 'FiInstagram',
        'FiFacebook', 'FiYoutube', 'FiSmile', 'FiThumbsUp', 'FiTrendingUp', 'FiZap'
    ];

    const openIconModal = () => {
        setLastSelection(editor.state.selection);
        setModal({ open: true, type: 'icon', value: '', title: 'Choisir une icône' });
    };

    const insertIcon = (iconName) => {
        if (!editor) return;
        const color = editor.getAttributes('textStyle').color || 'currentColor';
        const fontSize = editor.getAttributes('textStyle').fontSize || '24px';

        setModal({ ...modal, open: false });

        editor.commands.focus();
        if (lastSelection) {
            editor.view.dispatch(editor.state.tr.setSelection(lastSelection));
        }

        editor.chain()
            .insertContent({
                type: 'tiptapIcon',
                attrs: { name: iconName, size: fontSize.includes('px') ? fontSize : '24px', color }
            })
            .run();

        setLastSelection(null);
    };

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                history: true,
                bulletList: false, // On désactive les versions blocs standards
                orderedList: false,
                listItem: false,
            }),
            Placeholder.configure({ placeholder }),
            Link.configure({ openOnClick: false, autolink: true }),
            ResizableImage,
            TextAlignInline,
            BulletInline,
            OrderedInline,
            Underline,
            TextStyle,
            Color,
            FontSize,
            FontFamily,
            LineHeight,
            IconNode,
            Accent,
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            TaskList,
            TaskItem.configure({ nested: true }),
        ],
        content: content,
        onUpdate: ({ editor }) => onChange(editor.getHTML()),
        onTransaction: () => setUpdate(prev => prev + 1),
    });

    if (!editor) return null;

    const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px', '30px', '36px', '48px', '64px'];
    const fontFamilies = [
        { label: 'Défaut', value: 'inherit' },
        { label: 'Outfit (Moderne)', value: 'Outfit, sans-serif' },
        { label: 'Inter (Propre)', value: 'Inter, sans-serif' },
        { label: 'Playfair (Élégant)', value: 'Playfair Display, serif' },
        { label: 'Montserrat (Fort)', value: 'Montserrat, sans-serif' },
        { label: 'Bebas (Titre)', value: 'Bebas Neue, sans-serif' },
        { label: 'Comfortaa', value: 'Comfortaa, sans-serif' },
        { label: 'Cinzel', value: 'Cinzel, serif' },
        { label: 'Lora', value: 'Lora, serif' },
        { label: 'Abril', value: 'Abril Fatface, cursive' },
        { label: 'Lobster', value: 'Lobster, cursive' },
        { label: 'Caveat', value: 'Caveat, cursive' },
        { label: 'Shadows', value: 'Shadows Into Light, cursive' },
        { label: 'Amatic', value: 'Amatic SC, cursive' },
        { label: 'Dancing', value: 'Dancing Script, cursive' },
        { label: 'Playwrite', value: 'Playwrite FR Trad, cursive' },
        { label: 'Pacifico', value: 'Pacifico, cursive' },
        { label: 'Sour Gummy', value: 'Sour Gummy, sans-serif' },
        { label: 'Space', value: 'Space Grotesk, sans-serif' },
        { label: 'Monospace', value: 'monospace' },
    ];

    const lineHeights = [
        { label: 'Standard', value: 'normal' },
        { label: 'Compact', value: '1' },
        { label: 'Serré', value: '1.2' },
        { label: 'Aéré', value: '1.6' },
        { label: 'Large', value: '2' },
    ];

    const currentFontSize = editor.getAttributes('textStyle').fontSize || '16px';
    const currentFontFamily = editor.getAttributes('textStyle').fontFamily || 'inherit';

    return (
        <div className="tiptap-editor">
            <div className="tiptap-toolbar">
                <div className="tiptap-select-wrapper">
                    <select className="tiptap-select family-select" value={currentFontFamily} onChange={(e) => e.target.value === 'inherit' ? editor.chain().focus().unsetFontFamily().run() : editor.chain().focus().setFontFamily(e.target.value).run()}>
                        {fontFamilies.map(font => <option key={font.value} value={font.value}>{font.label}</option>)}
                    </select>
                </div>
                <div className="tiptap-select-wrapper">
                    <select className="tiptap-select size-select" value={currentFontSize} onChange={(e) => e.target.value === '16px' ? editor.chain().focus().unsetFontSize().run() : editor.chain().focus().setFontSize(e.target.value).run()}>
                        {fontSizes.map(size => <option key={size} value={size}>{size}</option>)}
                    </select>
                </div>
                <div className="tiptap-select-wrapper">
                    <select className="tiptap-select height-select" value={editor.getAttributes('paragraph').lineHeight || 'normal'} onChange={(e) => editor.chain().focus().setLineHeight(e.target.value).run()} title="Interligne">
                        {lineHeights.map(lh => <option key={lh.value} value={lh.value}>{lh.label}</option>)}
                    </select>
                </div>
                <span className="separator" />
                <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={`btn-icon ${editor.isActive('bold') ? 'is-active' : ''}`} title="Gras"><b>B</b></button>
                <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={`btn-icon ${editor.isActive('italic') ? 'is-active' : ''}`} title="Italique"><i>I</i></button>
                <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={`btn-icon ${editor.isActive('underline') ? 'is-active' : ''}`} title="Souligné"><u>U</u></button>
                <button type="button" onClick={() => editor.chain().focus().toggleMark('accent').run()} className={`btn-icon ${editor.isActive('accent') ? 'is-active' : ''}`} title="Texte en dégradé" style={{ color: '#0ea5e9' }}>✨</button>
                <button type="button" onClick={openLinkModal} className={`btn-icon ${editor.isActive('link') ? 'is-active' : ''}`} title="Lien">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                </button>
                <div className="color-picker-container" title="Couleur">
                    <input type="color" onInput={event => editor.chain().focus().setColor(event.target.value).run()} value={editor.getAttributes('textStyle').color || '#000000'} className="color-input-hidden" />
                    <div className="btn-icon color-preview-btn"><span style={{ fontWeight: 'bold' }}>A</span><div className="color-bar" style={{ backgroundColor: editor.getAttributes('textStyle').color || '#000' }}></div></div>
                </div>
                <span className="separator" />

                {/* LISTES GRANULAIRES (Comme l'alignement) */}
                <button type="button" onClick={() => editor.chain().focus().toggleBulletInline().run()} className={`btn-icon ${editor.isActive('bulletInline') ? 'is-active' : ''}`} title="Liste à puces">•≡</button>
                <button type="button" onClick={() => editor.chain().focus().toggleOrderedInline().run()} className={`btn-icon ${editor.isActive('orderedInline') ? 'is-active' : ''}`} title="Liste numérotée">1.</button>

                <span className="separator" />

                {(() => {
                    const handleAlign = (align) => {
                        if (editor.isActive('image')) {
                            editor.chain().focus().updateAttributes('image', { align }).run();
                        } else {
                            editor.chain().focus().setTextAlignInline(align).run();
                        }
                    };

                    const isAlignActive = (align) => {
                        if (editor.isActive('image')) {
                            return (editor.getAttributes('image').align || 'center') === align;
                        }
                        const current = editor.getAttributes('textAlignInline').align;
                        if (align === 'left') return current === 'left' || !editor.isActive('textAlignInline');
                        return current === align;
                    };

                    return (
                        <>
                            <button type="button" onClick={() => handleAlign('left')} className={`btn-icon ${isAlignActive('left') ? 'is-active' : ''}`} title="Aligner à gauche">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="17" y1="10" x2="3" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="17" y1="18" x2="3" y2="18"></line></svg>
                            </button>
                            <button type="button" onClick={() => handleAlign('center')} className={`btn-icon ${isAlignActive('center') ? 'is-active' : ''}`} title="Centrer">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="6" x2="3" y2="6"></line><line x1="17" y1="10" x2="7" y2="10"></line><line x1="19" y1="14" x2="5" y2="14"></line><line x1="21" y1="18" x2="3" y2="18"></line></svg>
                            </button>
                            <button type="button" onClick={() => handleAlign('right')} className={`btn-icon ${isAlignActive('right') ? 'is-active' : ''}`} title="Aligner à droite">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="10" x2="7" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="21" y1="18" x2="7" y2="18"></line></svg>
                            </button>
                        </>
                    );
                })()}

                <span className="separator" />
                <button type="button" onClick={addImage} className={`btn-icon ${uploading ? 'is-loading' : ''}`} title="Insérer une image" disabled={uploading}>
                    {uploading ? <span style={{ display: 'inline-block' }}>⏳</span> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>}
                </button>
                <button type="button" onClick={openIconModal} className="btn-icon" title="Insérer une icône">
                    <FiIcons.FiSmile />
                </button>
            </div>
            <EditorContent editor={editor} className="tiptap-content" />

            {modal.open && (
                <div className="tiptap-modal-backdrop" onClick={() => setModal({ ...modal, open: false })}>
                    <div className="tiptap-modal" style={{ width: modal.type === 'icon' ? 'min(92vw, 500px)' : undefined }} onClick={e => e.stopPropagation()}>
                        <div className="tiptap-modal-header">
                            <h3>{modal.title}</h3>
                        </div>
                        <div className="tiptap-modal-body">
                            {modal.type === 'link' ? (
                                <>
                                    <label>URL du lien</label>
                                    <input
                                        autoFocus
                                        placeholder="https://..."
                                        value={modal.value}
                                        onChange={e => setModal({ ...modal, value: e.target.value })}
                                        onKeyDown={e => e.key === 'Enter' && saveModalContent()}
                                    />
                                </>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', maxH: '300px', overflowY: 'auto', padding: '10px' }}>
                                    {popularIcons.map(name => {
                                        const IconComp = FiIcons[name];
                                        return (
                                            <button
                                                key={name}
                                                onClick={() => insertIcon(name)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '10px',
                                                    background: 'rgba(0,0,0,0.05)',
                                                    border: '1px solid transparent',
                                                    borderRadius: '8px',
                                                    fontSize: '1.5rem',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.1)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                                                title={name}
                                            >
                                                <IconComp />
                                            </button>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="tiptap-modal-footer">
                            <button className="tiptap-modal-btn cancel" onClick={() => setModal({ ...modal, open: false })}>Annuler</button>
                            {modal.type === 'link' && <button className="tiptap-modal-btn confirm" onClick={saveModalContent}>Confirmer</button>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TiptapEditor;
